// User Profile Service Types
// Base URL: http://localhost:17878/api/v1

export enum UserProfileStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  NON_BINARY = 'NON_BINARY',
  PREFER_NOT_TO_SAY = 'PREFER_NOT_TO_SAY',
  OTHER = 'OTHER',
}

export enum SkillLevel {
  FOUNDATIONAL = 'FOUNDATIONAL',
  BASIC = 'BASIC',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  EXPERT = 'EXPERT',
  MASTERY = 'MASTERY',
}

// Profile-level Skill (references master data skill by skillId)
export interface ProfileSkill {
  skillId: string; // Max 50 chars - references master data skill
  name?: string; // Max 100 chars - optional display name
  level?: SkillLevel;
  verified?: boolean;
}

// Profile-level Occupation (references master data occupation by occupationId)
export interface ProfileOccupation {
  occupationId: string; // Max 50 chars - references master data occupation
  name?: string; // Max 100 chars - optional display name
  verified?: boolean;
}

// Profile-level Pain Point
export interface ProfilePainPoint {
  painPointId: string; // Max 50 chars
  description?: string; // Max 200 chars
}

// Phone Number
export interface PhoneNumber {
  countryCode: string;
  number: string;
  verified?: boolean;
}

// Location Data (matches API schema)
export interface LocationData {
  latitude?: number;
  longitude?: number;
  country?: string;
  state?: string;
  city?: string;
  postalCode?: string;
  timezone?: string;
  isPrecise?: boolean;
}

// Preferences (matches API schema - used in both response and request)
export interface Preferences {
  preferredLanguage?: string;
  preferredTheme?: string;
  timezone?: string;
  additionalPreferences?: Record<string, unknown>;
}

// GDPR Compliance (matches API schema - used in both response and request)
export interface GdprCompliance {
  dataProcessingConsent?: boolean;
  consentGivenAt?: string;
  consentWithdrawnAt?: string;
  dataExportRequested?: boolean;
  dataExportRequestedAt?: string;
  dataDeletionRequested?: boolean;
  dataDeletionRequestedAt?: string;
  dataDeletedAt?: string;
  legalBasis?: string;
  dataRetentionPeriodDays?: number;
}

// Terms Acceptance (matches API schema - used in both response and request)
export interface TermsAcceptance {
  termsVersion?: string;
  acceptedAt?: string;
  acceptedIpAddress?: string;
  acceptedUserAgent?: string;
}

// User Consent (matches API schema - used in both response and request)
export interface UserConsent {
  termsAccepted?: boolean;
  privacyPolicyAccepted?: boolean;
  marketingEmails?: boolean;
  smsNotifications?: boolean;
  pushNotifications?: boolean;
  dataAnalytics?: boolean;
  thirdPartySharing?: boolean;
  cookieConsent?: boolean;
  consentTimestamp?: string;
  consentVersion?: string;
  consentIpAddress?: string;
}

// Bio Structure (nested in UserProfile)
export interface Bio {
  // Summaries
  summary?: string; // Max 2000 chars
  detailedBio?: string; // Max 5000 chars
  profileSummary?: string; // Max 2000 chars

  // Career & Professional
  currentRole?: string; // Max 100 chars
  currentOccupation?: ProfileOccupation;
  currentCompany?: string; // Max 100 chars
  yearsOfExperience?: number; // 0-70
  educationLevel?: string; // Max 50 chars
  careerStage?: string;
  industry?: string; // Max 100 chars

  // Skills & Expertise
  skills?: ProfileSkill[];
  interests?: string[];
  painPoints?: ProfilePainPoint[];
  occupations?: ProfileOccupation[];

  // Goals & Aspirations
  winsLookingFor?: string[];
  professionalGoals?: string[];

  // Additional Info
  facts?: string[];
}

// User Profile Core Structure
export interface UserProfile {
  // Identity
  id: string;
  userId: string; // Unique from identity provider
  userName?: string;
  email: string;
  emailVerified?: boolean;

  // Personal Info
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string; // Format: YYYY-MM-DD
  gender: Gender;
  phoneNumber?: PhoneNumber;

  // Profile Content
  bio?: Bio;
  profilePictureId?: string; // Content management ID
  profileCompletionPercentage?: number;

  // Location & Preferences
  location?: LocationData;
  preferences?: Preferences;

  // Status & Activity
  status: UserProfileStatus;
  isOnboardingCompleted?: boolean;
  lastActiveAt?: string; // ISO datetime

  // Compliance & Legal
  gdprCompliance?: GdprCompliance;
  termsAcceptance?: TermsAcceptance;
  userConsent?: UserConsent;

  // References
  sessionId?: string;
  externalRefId?: string; // Weaviate object ID
  enrichmentData?: Record<string, unknown>;

  // Audit
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  lastModifiedBy?: string;
}

// API Request/Response Types
export interface UserProfileResponse {
  data: UserProfile;
  message?: string;
}

export interface PaginatedUserProfileResponse {
  content: UserProfile[];
  page?: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
}

// Update Request Types
// Note: PUT /api/v1/profiles/{userId} requires: gdprCompliance, preferences, termsAcceptance, userConsent
// For partial updates, use the specific PATCH endpoints instead
export interface UserProfileUpdateRequest {
  userName?: string;
  email?: string;
  emailVerified?: boolean;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string; // YYYY-MM-DD
  gender?: Gender;
  phoneNumber?: PhoneNumber;
  bio?: Bio;
  status?: UserProfileStatus;
  isOnboardingCompleted?: boolean;
  profilePictureId?: string;
  profileCompletionPercentage?: number;
  preferences: Preferences; // Required for PUT endpoint
  gdprCompliance: GdprCompliance; // Required for PUT endpoint
  termsAcceptance: TermsAcceptance; // Required for PUT endpoint
  userConsent: UserConsent; // Required for PUT endpoint
  location?: LocationData;
  enrichmentData?: Record<string, unknown>;
}

export interface BioUpdateRequest {
  summary?: string;
  detailedBio?: string;
  profileSummary?: string;
  skills?: ProfileSkill[];
  interests?: string[];
  painPoints?: ProfilePainPoint[];
  occupations?: ProfileOccupation[];
  winsLookingFor?: string[];
  professionalGoals?: string[];
  currentRole?: string;
  currentOccupation?: ProfileOccupation;
  currentCompany?: string;
  yearsOfExperience?: number;
  educationLevel?: string;
  careerStage?: string;
  industry?: string;
  facts?: string[];
}

// PreferencesUpdateRequest (for PATCH endpoint - matches API schema)
export interface PreferencesUpdateRequest {
  preferredLanguage?: string;
  preferredTheme?: string;
  timezone?: string;
  additionalPreferences?: Record<string, unknown>;
}

// GdprComplianceUpdateRequest (for PATCH endpoint - matches API schema)
export interface GdprComplianceUpdateRequest {
  dataProcessingConsent?: boolean;
  consentGivenAt?: string;
  consentWithdrawnAt?: string;
  dataExportRequested?: boolean;
  dataExportRequestedAt?: string;
  dataDeletionRequested?: boolean;
  dataDeletionRequestedAt?: string;
  dataDeletedAt?: string;
  legalBasis?: string;
  dataRetentionPeriodDays?: number;
}

// TermsAcceptanceUpdateRequest (for PATCH endpoint - matches API schema)
export interface TermsAcceptanceUpdateRequest {
  termsVersion?: string;
  acceptedAt?: string;
  acceptedIpAddress?: string;
  acceptedUserAgent?: string;
}

// UserConsentUpdateRequest (for PATCH endpoint - matches API schema)
export interface UserConsentUpdateRequest {
  termsAccepted?: boolean;
  privacyPolicyAccepted?: boolean;
  marketingEmails?: boolean;
  smsNotifications?: boolean;
  pushNotifications?: boolean;
  dataAnalytics?: boolean;
  thirdPartySharing?: boolean;
  cookieConsent?: boolean;
  consentTimestamp?: string;
  consentVersion?: string;
  consentIpAddress?: string;
}

// Bulk Operations
export interface BulkProfileUpdate {
  userId: string;
  updateData: UserProfileUpdateRequest;
}

export interface BulkUpdateRequest {
  updates: BulkProfileUpdate[];
}

export interface BulkUpdateResult {
  index: number;
  userId: string;
  success: boolean;
  error?: string;
}

export interface BulkUpdateResponse {
  totalRequested: number;
  successCount: number;
  failureCount: number;
  results: BulkUpdateResult[];
}

// Analytics
export interface ProfileAnalytics {
  totalProfiles: number;
  activeUsers: number;
  inactiveUsers: number;
  pendingOnboarding: number;
  averageCompletionPercentage: number;
  newUsersThisWeek: number;
  [key: string]: unknown;
}

// Activity Tracking
export interface ActivityRecord {
  userId: string;
  activityType: string;
  activityData?: Record<string, unknown>;
  timestamp: string;
}

export interface ActivitySummary {
  userId: string;
  period: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  totalActivities: number;
  activitiesByType: Record<string, number>;
  lastActivityAt?: string;
}

// Profile Completion Details
export enum CompletionCategory {
  CRITICAL = 'CRITICAL',
  HIGH_VALUE = 'HIGH_VALUE',
  MEDIUM_VALUE = 'MEDIUM_VALUE',
  LOW_VALUE = 'LOW_VALUE',
}

export interface CompletionBreakdown {
  weightPercentage: number;
  totalFields: number;
  completedFields: number;
  pendingFields: number;
  achievedWeight: number;
}

export interface MissingField {
  fieldName: string;
  displayName: string;
  category: CompletionCategory;
  weight: number;
  description: string;
  example: string;
}

export interface ProfileCompletionDetails {
  completionPercentage: number;
  totalFields: number;
  completedFields: number;
  pendingFields: number;
  breakdown: {
    critical: CompletionBreakdown;
    highValue: CompletionBreakdown;
    mediumValue: CompletionBreakdown;
    lowValue: CompletionBreakdown;
  };
  missingFields: MissingField[];
  lastCalculatedAt: string;
}







